import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Badge, Dropdown, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  FaDatabase,
  FaPlus,
  FaEdit,
  FaEye,
  FaCog,
  FaList,
  FaLayerGroup,
  FaUsers,
  FaArrowRight,
  FaInfoCircle,
  FaCheck,
  FaTrash,
  FaBolt,
  FaBars,
  FaTimes,
  FaUsersCog,
  FaTh,
  FaTags,
  FaSitemap,
  FaChevronRight,
  FaChevronLeft,
  FaChevronDown
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import FieldManager from './FieldManager';
import LayoutManager from './LayoutManager';
import RoleManager from './RoleManager';
import ActionManager from './ActionManager';
import RecordViewManager from './RecordViewManager';
import EditObject from '../layout/EditObject';
import './css/ObjectManager.css';
import { getEntitlementsSection, canAccessSetup } from '../../utils/entitlements';

/** New objects use the create-object API for standard fields and DDL; base_price is intentionally omitted for new objects (see backend objectconfig.model / schema.model). */

/** Resolve allow_explore from config, with schema/tenant override. Config may be: { allow_explore: false, demo: { allow_explore: true } }. */
const getEffectiveAllowExplore = (config, schemaOrTenantKey) => {
  if (!config || typeof config !== 'object') return true;
  const base = config?.allow_explore;
  const override =
    schemaOrTenantKey &&
      config[schemaOrTenantKey] &&
      typeof config[schemaOrTenantKey] === 'object' &&
      config[schemaOrTenantKey].hasOwnProperty('allow_explore')
      ? config[schemaOrTenantKey].allow_explore
      : undefined;
  const value = override !== undefined ? override : base;
  return !(value === false || value === 'false' || value === 0 || value === '0');
};

const ObjectManager = () => {
  const navigate = useNavigate();
  const location = window.location;
  const objectManagerConfig = getEntitlementsSection('objectManager', {});
  const allowObjectCreate =
    objectManagerConfig?.allow_create ??
    objectManagerConfig?.allowCreate ??
    true;
  // Standard objects can be edited only when enable_standard in company_subscription/company_license (same as TabManager)
  const enableStandardObject =
    objectManagerConfig?.enable_standard === true ||
    objectManagerConfig?.enableStandard === true;
  const canEditObject = (object) => !object?.is_standard || enableStandardObject;
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedObject, setSelectedObject] = useState(null);
  const [activeView, setActiveView] = useState('objects'); // objects, fields, layouts, recordviews, actions
  const [showFieldManager, setShowFieldManager] = useState(false);
  const [showRoleManager, setShowRoleManager] = useState(false);
  const [showEditObject, setShowEditObject] = useState(false);
  const [editObjectMode, setEditObjectMode] = useState('create');
  const [editObjectData, setEditObjectData] = useState(null);
  const [trashedObjects, setTrashedObjects] = useState([]);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [loadingTrashedObjects, setLoadingTrashedObjects] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoringFromUrl, setRestoringFromUrl] = useState(false);
  const [sortBy, setSortBy] = useState('name'); // 'name', 'label', 'type', 'created'
  const [sortOrder, setSortOrder] = useState('asc');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // 'asc', 'desc'
  const [isSorting, setIsSorting] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  /** When set, LayoutManager opens this layout once (deep link from bulk assignment page). */
  const [pendingLayoutOpenId, setPendingLayoutOpenId] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    const objectNameParam = urlParams.get('object');
    const layoutIdParam = urlParams.get('layoutId');

    console.log('ObjectManager - URL params:', { viewParam, objectNameParam, layoutIdParam });

    if (viewParam && objectNameParam) {
      setRestoringFromUrl(true);
      const findObjectByName = async () => {
        try {
          const api = new DataApi('objectconfig');
          const response = await api.fetchAll();

          if (response?.data?.success) {
            const objectsWithFields = response.data.data || [];
            const foundObject = objectsWithFields.find(obj =>
              obj.object_name === objectNameParam && !obj.is_deleted
            );

            console.log('ObjectManager - Found object from URL:', foundObject);

            const schemaOrTenantKey =
              typeof localStorage !== 'undefined' ? localStorage.getItem('tenantcode') || undefined : undefined;
            const canExplore = (obj) => {
              const raw = obj?.config;
              const config = !raw ? {} : typeof raw === 'string' ? (() => { try { return JSON.parse(raw) || {}; } catch { return {}; } })() : raw;
              return getEffectiveAllowExplore(config, schemaOrTenantKey);
            };

            if (foundObject && canExplore(foundObject)) {
              setSelectedObject(foundObject);
              setActiveView(viewParam);
              if (layoutIdParam) {
                setPendingLayoutOpenId(layoutIdParam);
              }
              console.log('ObjectManager - State restored from URL:', { object: foundObject.object_name, view: viewParam });
            } else {
              console.log('ObjectManager - Object not found or not explorable, clearing URL');
              const url = new URL(window.location);
              url.searchParams.delete('view');
              url.searchParams.delete('object');
              url.searchParams.delete('layoutId');
              window.history.replaceState({}, '', url);
            }
          }
        } catch (error) {
          console.error('Error restoring state from URL:', error);
          const url = new URL(window.location);
          url.searchParams.delete('view');
          url.searchParams.delete('object');
          url.searchParams.delete('layoutId');
          window.history.replaceState({}, '', url);
        } finally {
          setRestoringFromUrl(false);
        }
      };

      findObjectByName();
    }
  }, []);


  useEffect(() => {
    if (activeView !== 'objects' && selectedObject) {
      const url = new URL(window.location);
      url.searchParams.set('view', activeView);
      url.searchParams.set('object', selectedObject.object_name);
      if (pendingLayoutOpenId) {
        url.searchParams.set('layoutId', pendingLayoutOpenId);
      } else {
        url.searchParams.delete('layoutId');
      }
      window.history.replaceState({}, '', url);
      console.log('ObjectManager - URL updated:', url.toString());
    } else {

      const url = new URL(window.location);
      url.searchParams.delete('view');
      url.searchParams.delete('object');
      url.searchParams.delete('layoutId');
      window.history.replaceState({}, '', url);
      console.log('ObjectManager - URL cleared');
    }
  }, [activeView, selectedObject, pendingLayoutOpenId]);

  useEffect(() => {
    fetchObjects();
    fetchTrashedObjects(); // Load trashed objects count on page load
  }, []);

  const fetchObjects = async () => {
    try {
      setLoading(true);
      setError('');

      // Call the existing endpoint that fetches all objects with fields
      const api = new DataApi('objectconfig');
      const response = await api.fetchAll();

      console.log('ObjectManager API Response:', response);

      if (response?.data?.success) {
        const objectsWithFields = response.data.data || [];

        // Debug logging
        console.log('ObjectManager - Raw API response:', response.data);
        console.log('ObjectManager - First object structure:', objectsWithFields[0]);

        const objectManagerConfig = getEntitlementsSection('objectManager', {});
        const allowedStandardObjects = new Set(
          (objectManagerConfig?.allowedObjects || []).map(name => String(name).toLowerCase())
        );
        const restrictStandardObjects = objectManagerConfig?.mode === 'allowlist'
          && objectManagerConfig?.scope === 'standard'
          && allowedStandardObjects.size > 0;

        const parseObjectConfig = (rawConfig) => {
          if (!rawConfig) return {};
          if (typeof rawConfig === 'string') {
            try {
              const parsed = JSON.parse(rawConfig);
              return parsed && typeof parsed === 'object' ? parsed : {};
            } catch (error) {
              console.warn('Failed to parse object config:', error);
              return {};
            }
          }
          return typeof rawConfig === 'object' ? rawConfig : {};
        };

        const schemaOrTenantKey =
          typeof localStorage !== 'undefined' ? localStorage.getItem('tenantcode') || undefined : undefined;
        const canExploreObject = (obj) => {
          const config = parseObjectConfig(obj?.config);
          return getEffectiveAllowExplore(config, schemaOrTenantKey);
        };

        const isObjectAllowed = (obj) => {
          const nameLc = String(obj?.object_name || '').toLowerCase();
          // User is a system object; when Setup is licensed, do not gate it by objectManager.allowedObjects.
          if (nameLc === 'user' && canAccessSetup()) {
            return true;
          }
          if (!restrictStandardObjects) {
            return true;
          }
          if (!obj?.is_standard) {
            return true;
          }
          return allowedStandardObjects.has(nameLc);
        };

        // Filter out deleted objects, disallowed standard objects, and disallowed exploration objects
        const processedObjects = objectsWithFields
          .filter(obj => !obj.is_deleted)
          .filter(isObjectAllowed)
          .filter(canExploreObject)
          .map(obj => ({
            ...obj,
            field_count: obj.fields ? obj.fields.length : 0,
            layout_count: 0, // Not showing layout count as requested
            record_count: 0 // Will be calculated later if needed
          }));

        console.log('ObjectManager - Processed objects:', processedObjects);
        setObjects(processedObjects);
      } else {
        setObjects([]);
      }

    } catch (error) {
      console.error('Error fetching objects:', error);
      setObjects([]);
      setError('Failed to load objects: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleObjectSelect = (object) => {
    setSelectedObject(object);
    setActiveView('object-details');
  };

  const handleViewChange = (view) => {
    if (view === 'actions' && selectedObject) {
      // Navigate to object-specific actions URL
      navigate(`/setup/${selectedObject.object_name}/actions`);
    } else {
      setActiveView(view);
    }
  };

  const handleCreateObject = () => {
    if (!allowObjectCreate) {
      setError('Object creation is disabled for this tenant.');
      return;
    }
    setEditObjectMode('create');
    setEditObjectData(null);
    setShowEditObject(true);
  };

  const handleEditObject = (object) => {
    setEditObjectMode('edit');
    setEditObjectData(object);
    setShowEditObject(true);
  };

  const handleEditObjectSuccess = (createdObjectData) => {
    fetchObjects(); // Refresh the objects list

    // If this was a new object creation, immediately navigate to object detail page
    if (editObjectMode === 'create' && createdObjectData) {
      // Ensure the object has the required properties for handleObjectSelect
      const objectForSelection = {
        object_name: createdObjectData.objectName || createdObjectData.object_name || createdObjectData.name,
        object_label: createdObjectData.objectLabel || createdObjectData.object_label || createdObjectData.label,
        plural_label: createdObjectData.pluralLabel || createdObjectData.plural_label,
        id: createdObjectData.id || createdObjectData.objectConfig?.data?.id,
        field_count: 0,
        layout_count: 0,
        record_count: 0
      };

      console.log('ObjectManager - Created object data:', createdObjectData);
      console.log('ObjectManager - Object for selection:', objectForSelection);

      handleObjectSelect(objectForSelection);
    }
  };

  const fetchTrashedObjects = async () => {
    try {
      setLoadingTrashedObjects(true);
      const response = await ObjectConfigApi.getTrashedObjects();

      if (response?.success) {
        setTrashedObjects(response.data || []);
      } else {
        setError(response?.message || 'Failed to fetch trashed objects');
      }
    } catch (error) {
      console.error('Error fetching trashed objects:', error);
      setError('Failed to fetch trashed objects: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingTrashedObjects(false);
    }
  };

  const handleTrashObject = async (objectName) => {
    if (!window.confirm(`Are you sure you want to move "${objectName}" to trash?`)) {
      return;
    }

    try {
      setLoading(true);

      const response = await ObjectConfigApi.trashObject(objectName);

      if (response?.success) {
        setSuccess('Object moved to trash successfully!');
        setTimeout(() => setSuccess(''), 3000);

        // If we're currently viewing this object's details, navigate back to objects list
        if (selectedObject && selectedObject.object_name === objectName) {
          setSelectedObject(null);
          setActiveView('objects');
        }

        // Refresh the objects list to remove the deleted object and any related objects
        await fetchObjects();

        // Refresh trashed objects count
        fetchTrashedObjects();
      } else {
        setError(response?.message || 'Failed to move object to trash');
      }
    } catch (error) {
      console.error('Error trashing object:', error);
      setError('Failed to move object to trash: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentlyDeleteObject = async (objectName) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${objectName}"? This action cannot be undone and will remove the table completely.`)) {
      return;
    }

    try {
      setLoadingTrashedObjects(true);

      const response = await ObjectConfigApi.permanentlyDeleteObject(objectName);

      if (response?.success) {
        setTrashedObjects(trashedObjects.filter(obj => obj.object_name !== objectName));
        setSuccess('Object permanently deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);

        // Refresh trashed objects count immediately
        fetchTrashedObjects();

        // Close modal if no more trashed objects
        if (trashedObjects.length <= 1) {
          setShowTrashModal(false);
        }
      } else {
        setError(response?.message || 'Failed to permanently delete object');
      }
    } catch (error) {
      console.error('Error permanently deleting object:', error);
      setError('Failed to permanently delete object: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingTrashedObjects(false);
    }
  };

  const handleRestoreObject = async (objectName) => {
    try {
      setLoadingTrashedObjects(true);

      const response = await ObjectConfigApi.restoreObject(objectName);

      if (response?.success) {
        setTrashedObjects(trashedObjects.filter(obj => obj.object_name !== objectName));
        setSuccess('Object restored successfully!');
        setTimeout(() => setSuccess(''), 3000);

        // Refresh main objects list
        fetchObjects();

        // Refresh trashed objects count immediately
        fetchTrashedObjects();

        // Close modal if no more trashed objects
        if (trashedObjects.length <= 1) {
          setShowTrashModal(false);
        }
      } else {
        setError(response?.message || 'Failed to restore object');
      }
    } catch (error) {
      console.error('Error restoring object:', error);
      setError('Failed to restore object: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingTrashedObjects(false);
    }
  };


  const getObjectIcon = (objectName) => {
    if (!objectName) return '📄';

    const icons = {
      contact: '👤',
      lead: '🎯',
      account: '🏢',
      opportunity: '💰',
      task: '📋',
      case: '📞',
      product: '📦',
      pricebook: '📊',
      user: '👨‍💼',
      role: '🔐'
    };
    return icons[objectName.toLowerCase()] || '📄';
  };

  const handleSortChange = (newSortBy) => {
    setIsSorting(true);

    if (sortBy === newSortBy) {
      // If clicking the same sort field, toggle order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different sort field, set to ascending
      setSortBy(newSortBy);
      setSortOrder('asc');
    }

    // Reset sorting state after a brief delay
    setTimeout(() => setIsSorting(false), 100);
  };

  // Filter and sort objects
  const filteredObjects = objects
    .filter(object => {
      const term = searchTerm.toLowerCase();
      const typeLabel = object.is_standard ? 'standard' : 'custom';
      return object.object_name.toLowerCase().includes(term) ||
        (object.object_label && object.object_label.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (object.description && object.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        typeLabel.includes(term);
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = a.object_name.toLowerCase();
          bValue = b.object_name.toLowerCase();
          break;
        case 'label':
          aValue = (a.object_label || a.object_name).toLowerCase();
          bValue = (b.object_label || b.object_name).toLowerCase();
          break;
        case 'created':
          aValue = new Date(a.createddate || 0);
          bValue = new Date(b.createddate || 0);
          break;
        case 'type':
          aValue = a.is_standard ? 'standard' : 'custom';
          bValue = b.is_standard ? 'standard' : 'custom';
          break;
        default:
          aValue = a.object_name.toLowerCase();
          bValue = b.object_name.toLowerCase();
      }

      if (sortBy === 'created') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });



  const renderObjectsView = () => (
    <div className="objects-view">
      <div className="view-header">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h2>
              <FaDatabase className="me-3" />
              Object Manager
            </h2>
            <p className="text-muted">
              Manage your CRM objects, fields, layouts, and roles
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setShowTrashModal(true);
                if (trashedObjects.length === 0) {
                  fetchTrashedObjects();
                }
              }}
            >
              <FaTrash className="me-1" />
              View Trash
              {trashedObjects.length > 0 && (
                <Badge bg="danger" className="ms-1">
                  {trashedObjects.length}
                </Badge>
              )}
            </Button>
            {allowObjectCreate && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleCreateObject}
              >
                <FaPlus className="me-1" />
                Create New Object
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Search Header - Always Visible */}
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaList className="me-2" />
              CRM Objects ({filteredObjects.length} of {objects.length})
              {sortBy && (
                <small className="text-muted ms-2">
                  - Sorted by {sortBy === 'name' ? 'Name' : sortBy === 'label' ? 'Label' : sortBy === 'type' ? 'Type' : 'Created Date'} ({sortOrder === 'asc' ? 'A-Z' : 'Z-A'})
                  {isSorting && <span className="ms-1">⏳</span>}
                </small>
              )}
            </h5>
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 small text-white">Search:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search objects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '200px' }}
              />
              {searchTerm && (
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => setSearchTerm('')}
                >
                  Clear
                </Button>
              )}
              <label className="form-label mb-0 small text-white">Sort by:</label>
              <select
                className="form-select form-select-sm"
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                style={{ width: '120px' }}
              >
                <option value="name">Name</option>
                <option value="label">Label</option>
                <option value="type">Type</option>
                <option value="created">Created</option>
              </select>
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </Button>
              {(sortBy !== 'name' || sortOrder !== 'asc') && (
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => {
                    setSortBy('name');
                    setSortOrder('asc');
                  }}
                  title="Reset to default sort (Name A-Z)"
                >
                  Reset
                </Button>
              )}
              <Dropdown className="ms-2">
                <Dropdown.Toggle variant="outline-light" size="sm" id="view-mode-dropdown">
                  {viewMode === 'table' ? <FaList /> : <FaTh />}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setViewMode('table')}>
                    <FaList className="me-2" />
                    Table View
                    {viewMode === 'table' && <span className="ms-2">✓</span>}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setViewMode('card')}>
                    <FaTh className="me-2" />
                    Card View
                    {viewMode === 'card' && <span className="ms-2">✓</span>}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Card.Header>
      </Card>

      {loading ? (
        <div className="text-center py-5">
          <div className="loading-spinner mb-3"></div>
          <p>Loading objects...</p>
        </div>
      ) : filteredObjects.length === 0 ? (
        <div className="text-center py-5">
          <div className="empty-state">
            <Card>
              <Card.Body>
                <h6>No Objects Found</h6>
                <p className="text-muted">
                  {objects.length === 0
                    ? 'No CRM objects have been configured yet. Contact your administrator to set up objects.'
                    : 'No objects match your search criteria. Try adjusting your search term.'
                  }
                </p>
              </Card.Body>
            </Card>
          </div>
        </div>
      ) : viewMode === 'table' ? (
        <div className="om-table-wrapper">
          <table className="om-data-table">
            <thead>
              <tr>
                <th style={{ width: '45%' }}>
                  <div className="om-th-content">
                    Object Label
                    <FaChevronDown className="om-sort-icon" />
                  </div>
                </th>
                <th style={{ width: '20%' }}>
                  <div className="om-th-content">
                    API Name
                    <FaChevronDown className="om-sort-icon" />
                  </div>
                </th>
                <th style={{ width: '15%' }}>
                  <div className="om-th-content">
                    Type
                    <FaChevronDown className="om-sort-icon" />
                  </div>
                </th>
                <th style={{ width: '20%' }}>
                  <div className="om-th-content">
                    Actions
                    <FaChevronDown className="om-sort-icon" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredObjects.map((object, index) => (
                <tr
                  key={object.id}
                  className="om-table-row"
                  onClick={() => handleObjectSelect(object)}
                >
                  <td>
                    <div className="om-object-cell">
                      <span className="om-obj-icon">{getObjectIcon(object.object_name)}</span>
                      <div className="om-obj-info">
                        <div className="om-obj-label">{object.object_label || object.object_name}</div>
                        <div className="om-obj-desc">
                          {object.description || `Manage ${object.object_label || object.object_name} data`}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <Badge bg="light" text="dark" className="me-1">
                      {object.object_name}
                    </Badge>
                  </td>
                  <td>
                    <span className="om-type-badge">{object.is_standard ? 'Standard' : 'Custom'}</span>
                  </td>
                  <td>
                    <div className="om-action-btns">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="om-manage-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleObjectSelect(object);
                        }}
                      >
                        <FaCog className="me-1" />
                        Manage
                      </Button>
                      {canEditObject(object) && (
                        <Button
                          variant="link"
                          size="sm"
                          className="om-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditObject(object);
                          }}
                          title="Edit"
                        >
                          <FaEdit />
                        </Button>
                      )}
                      {!object.is_standard && (
                        <Button
                          variant="link"
                          size="sm"
                          className="om-icon-btn om-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTrashObject(object.object_name);
                          }}
                          title="Delete"
                        >
                          <FaTrash />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Card>
          <Card.Body className="p-0">
            <div className="object-list">
              {filteredObjects.map((object, index) => (
                <div
                  key={object.id}
                  className={`object-list-item ${index === objects.length - 1 ? 'last-item' : ''}`}
                  onClick={() => handleObjectSelect(object)}
                >
                  <div className="object-list-content">
                    <div className="object-info">
                      <div className="object-icon-wrapper">
                        <span className="object-icon">{getObjectIcon(object.object_name)}</span>
                      </div>
                      <div className="object-details">
                        <h6 className="object-name">{object.object_label || object.object_name}</h6>
                        <p className="object-description">
                          {object.description || `Manage ${object.object_label || object.object_name} data`}
                        </p>
                        <div className="object-meta">
                          <Badge bg="secondary" className="me-2">{object.object_name}</Badge>
                        </div>
                      </div>
                    </div>

                    <div className="object-actions">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleObjectSelect(object);
                        }}
                      >
                        <FaCog className="me-1" />
                        Manage
                      </Button>
                      {canEditObject(object) && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditObject(object);
                          }}
                        >
                          <FaEdit className="me-1" />
                          Edit
                        </Button>
                      )}
                      {!object.is_standard && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTrashObject(object.object_name);
                          }}
                        >
                          <FaTrash className="me-1" />
                          Trash
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );

  const renderObjectDetailsView = () => (
    <div className="object-details-view">
      <div className="view-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setSelectedObject(null);
                setActiveView('objects');
              }}
              className="mb-3"
            >
              ← Back to Objects
            </Button>
            <h2>
              <span className="object-icon me-2">{getObjectIcon(selectedObject.object_name)}</span>
              {selectedObject.object_label || selectedObject.object_name}
            </h2>
            <p className="text-muted">
              Choose what you want to manage for this object
            </p>
          </div>
        </div>
      </div>

      {/* Object Information Card - First */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FaInfoCircle className="me-2" />
                  Object Information
                </h5>
                <div className="d-flex gap-2">
                  {canEditObject(selectedObject) && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEditObject(selectedObject)}
                    >
                      <FaEdit className="me-1" />
                      Edit Object
                    </Button>
                  )}
                  {!selectedObject.is_standard && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleTrashObject(selectedObject.object_name)}
                    >
                      <FaTrash className="me-1" />
                      Delete Object
                    </Button>
                  )}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="info-item">
                    <strong>Object Name:</strong> {selectedObject.object_name}
                  </div>
                  <div className="info-item">
                    <strong>Object Label:</strong> {selectedObject.object_label || 'N/A'}
                  </div>
                  <div className="info-item">
                    <strong>Plural Label:</strong> {selectedObject.plural_label || 'N/A'}
                  </div>
                  <div className="info-item">
                    <strong>Description:</strong> {selectedObject.description || 'No description available'}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="info-item">
                    <strong>Standard Object:</strong> {selectedObject.is_standard ? 'Yes' : 'No'}
                  </div>
                  <div className="info-item">
                    <strong>Activity Enabled:</strong> {selectedObject.allow_activity ? 'Yes' : 'No'}
                  </div>
                  <div className="info-item">
                    <strong>Created:</strong> {new Date(selectedObject.createddate).toLocaleDateString()}
                  </div>
                  <div className="info-item">
                    <strong>Last Modified:</strong> {new Date(selectedObject.lastmodifieddate).toLocaleDateString()}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Management Option Cards */}
      <Row>
        <Col lg={3} md={6} className="mb-4">
          <Card className="management-option-card" onClick={() => handleViewChange('fields')}>
            <Card.Body className="text-center">
              <div className="management-icon-wrapper">
                <FaList size={48} className="text-primary" />
              </div>
              <h4 className="mt-3 mb-2">Field Management</h4>
              <p className="text-muted mb-3">
                Create, edit, and organize fields for this object. Define data types, validation rules, and field properties.
              </p>
              <div className="management-features">
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Add custom fields</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Set field types</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Configure validation</span>
                </div>
              </div>
              <Button variant="primary" size="lg" className="mt-3 w-100">
                <FaList className="me-2" />
                Manage Fields
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-4">
          <Card className="management-option-card" onClick={() => handleViewChange('layouts')}>
            <Card.Body className="text-center">
              <div className="management-icon-wrapper">
                <FaLayerGroup size={48} className="text-info" />
              </div>
              <h4 className="mt-3 mb-2">Layout Management</h4>
              <p className="text-muted mb-3">
                Create and customize page layouts. Organize fields into sections and control the user interface.
              </p>
              <div className="management-features">
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Design page layouts</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Organize sections</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Control field display</span>
                </div>
              </div>
              <Button variant="info" size="lg" className="mt-3 w-100">
                <FaLayerGroup className="me-2" />
                Manage Layouts
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-4">
          <Card className="management-option-card" onClick={() => handleViewChange('recordviews')}>
            <Card.Body className="text-center">
              <div className="management-icon-wrapper">
                <FaTags size={48} className="text-success" />
              </div>
              <h4 className="mt-3 mb-2">Record view management</h4>
              <p className="text-muted mb-3">
                Define record views for this object (types and presentation context). Use with layouts in a later step.
              </p>
              <div className="management-features">
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Create record views</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Labels and defaults</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Active and default flags</span>
                </div>
              </div>
              <Button variant="success" size="lg" className="mt-3 w-100">
                <FaTags className="me-2" />
                Manage record views
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-4">
          <Card className="management-option-card" onClick={() => handleViewChange('actions')}>
            <Card.Body className="text-center">
              <div className="management-icon-wrapper">
                <FaBolt size={48} className="text-warning" />
              </div>
              <h4 className="mt-3 mb-2">Action Management</h4>
              <p className="text-muted mb-3">
                Create and manage custom actions for this object. Define workflows and user interactions.
              </p>
              <div className="management-features">
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Create actions</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Define workflows</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="text-success me-2" />
                  <span>Custom interactions</span>
                </div>
              </div>
              <Button variant="warning" size="lg" className="mt-3 w-100">
                <FaBolt className="me-2" />
                Manage Actions
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );

  const renderFieldsView = () => (
    <div className="fields-view">
      <FieldManager
        objectName={selectedObject.object_name}
        objectId={selectedObject.id}
        show={true}
        onHide={() => {
          // When FieldManager is hidden, go back to object details
          handleViewChange('object-details');
        }}
        onNavigate={handleViewChange}
        selectedObject={selectedObject}
      />
      {/* Debug logging */}
      {console.log('ObjectManager - selectedObject.id:', selectedObject.id)}
      {console.log('ObjectManager - selectedObject.id type:', typeof selectedObject.id)}
      {console.log('ObjectManager - selectedObject.id length:', selectedObject.id ? selectedObject.id.length : 'undefined')}
    </div>
  );

  const renderLayoutsView = () => (
    <div className="layouts-view">

      <div className="view-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleViewChange('object-details')}
              className="mb-3"
            >
              ← Back to Object
            </Button>

          </div>
        </div>
      </div>
      <LayoutManager
        objectName={selectedObject.object_name}
        initialLayoutId={pendingLayoutOpenId}
        onInitialLayoutOpened={() => {
          setPendingLayoutOpenId(null);
          const url = new URL(window.location);
          if (url.searchParams.has('layoutId')) {
            url.searchParams.delete('layoutId');
            window.history.replaceState({}, '', url);
          }
        }}
      />
    </div>
  );

  const renderRolesView = () => (
    <div className="roles-view">
      <div className="view-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleViewChange('object-details')}
              className="mb-3"
            >
              ← Back to Object
            </Button>
            <h2>
              <FaUsers className="me-3" />
              Role Management - {selectedObject.object_label || selectedObject.object_name}
            </h2>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowRoleManager(true)}
          >
            <FaPlus className="me-1" />
            Assign Roles
          </Button>
        </div>
      </div>

      <RoleManager
        objectName={selectedObject.object_name}
        show={showRoleManager}
        onHide={() => setShowRoleManager(false)}
      />
    </div>
  );

  const renderActionsView = () => (
    <div className="actions-view">
      <div className="view-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleViewChange('object-details')}
              className="mb-3"
            >
              ← Back to Object
            </Button>
            <h2>
              <FaBolt className="me-3" />
              Action Management - {selectedObject.object_label || selectedObject.object_name}
            </h2>
          </div>
        </div>
      </div>

      <ActionManager
        objectName={selectedObject.object_name}
        show={true}
        onHide={() => {
          // When ActionManager is hidden, go back to object details
          handleViewChange('object-details');
        }}
      />
    </div>
  );

  const renderRecordViewsView = () => (
    <div className="recordviews-view">
      <RecordViewManager
        objectName={selectedObject.object_name}
        onBack={() => handleViewChange('object-details')}
      />
    </div>
  );


  return (
    <div className="object-manager" style={{ padding: 0, margin: 0, width: '100%' }}>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {restoringFromUrl && (
        <Alert variant="info" dismissible onClose={() => setRestoringFromUrl(false)}>
          Restoring state from URL parameters...
        </Alert>
      )}

      {activeView === 'objects' && renderObjectsView()}
      {activeView === 'object-details' && selectedObject && renderObjectDetailsView()}
      {activeView === 'fields' && selectedObject && renderFieldsView()}
      {activeView === 'layouts' && selectedObject && renderLayoutsView()}
      {activeView === 'recordviews' && selectedObject && renderRecordViewsView()}
      {activeView === 'actions' && selectedObject && renderActionsView()}

      {/* EditObject Modal */}
      <EditObject
        show={showEditObject}
        onHide={() => setShowEditObject(false)}
        mode={editObjectMode}
        objectData={editObjectData}
        onSuccess={handleEditObjectSuccess}
      />

      {/* Trash Modal */}
      <Modal show={showTrashModal} onHide={() => setShowTrashModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTrash className="me-2 text-danger" />
            Trashed Objects
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingTrashedObjects ? (
            <div className="text-center py-4">
              <div className="loading-spinner mb-3"></div>
              <p>Loading trashed objects...</p>
            </div>
          ) : trashedObjects.length === 0 ? (
            <div className="text-center py-4">
              <FaTrash className="text-muted mb-3" style={{ fontSize: '3rem' }} />
              <h5 className="text-muted">No trashed objects</h5>
              <p className="text-muted">Objects you move to trash will appear here</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead>
                  <tr>
                    <th>Object Name</th>
                    <th>Object Label</th>
                    <th>Trashed Date</th>
                    <th style={{ width: 200 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trashedObjects.map((object) => (
                    <tr key={object.id}>
                      <td>
                        <code>{object.object_name}</code>
                      </td>
                      <td>
                        {object.object_label || object.object_name}
                      </td>
                      <td>
                        {object.lastmodifieddate ? new Date(object.lastmodifieddate).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => handleRestoreObject(object.object_name)}
                          >
                            Restore
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handlePermanentlyDeleteObject(object.object_name)}
                          >
                            Delete Permanently
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTrashModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ObjectManager;
